// web/src/player/captionEngine.ts
import type { WordTiming, WordState, CaptionWord, VisibleWord } from "./types";

export const CAPTION = {
  TIMING_BUFFER: 0.05,          // 50ms (iOS L191)
  MIN_HIGHLIGHT_DURATION: 0.15, // 150ms (iOS L42)
  VISIBLE_WORD_COUNT: 5,        // (iOS CaptionModels L19)
  // Catch-up resync: a forward jump larger than EITHER threshold snaps straight to the
  // spoken word instead of ratcheting through the backlog at 1 word/150ms (which visibly
  // lags the voice after tab refocus, a rebuffer, a forward seek, or a run of bunched
  // interpolated timings). Small jumps keep the smooth 1-step iOS-parity advance.
  CATCH_UP_MAX_LAG_WORDS: 3,
  CATCH_UP_MAX_LAG_SEC: 1.5,
} as const;

function cleanWord(w: string): string {
  let c = w.replace(/\[.*?\]/g, "").replace(/\[\w*$/g, "").replace(/^\w*\]/g, "");
  c = c.replace(/[\[\]]/g, "").replace(/\*\*/g, "").replace(/\*/g, "").replace(/__/g, "").replace(/_/g, "");
  return c.trim();
}

function isEndOfSentence(word: string): boolean {
  const t = word.trim();
  if (!t) return false;
  if (t.includes(".") || t.includes("!") || t.includes("?")) {
    for (let i = t.length - 1; i >= 0; i--) {
      const ch = t[i];
      if (ch === "." || ch === "!" || ch === "?") return true;
      if (/[a-z0-9]/i.test(ch)) return false;
    }
  }
  return false;
}

export class CaptionEngine {
  private words: CaptionWord[] = [];
  private currentWordIndex = 0;
  private lastHighlightedIndex = -1;
  private wordHighlightStartTime = 0;
  private allWordsCompleted = false;
  private visible: VisibleWord[] = [];
  // How many words are shown at once. Default 5 (iOS); narrower aspects (9:16) set this lower so
  // the caption stays on ONE line instead of wrapping. Config, not state — survives reset().
  private windowSize: number = CAPTION.VISIBLE_WORD_COUNT;
  // Reconciled dialogue end (see wordTimingRepair.reconcileDialogueEnd). When set, the
  // all-words-completed blank waits for THIS time instead of the raw last word end — so an
  // STT-undershot final timing can't blank the captions while the voice is still talking.
  // Config, not state — survives reset().
  private completionSec: number | null = null;

  /** Set how many words show at once (clamped ≥1). Takes effect on the next update(). */
  setWindowSize(n: number) {
    this.windowSize = Math.max(1, Math.floor(n));
  }

  /** Override when the caption layer considers dialogue finished (null = raw last word end). */
  setCompletionSec(sec: number | null) {
    this.completionSec = sec !== null && isFinite(sec) && sec > 0 ? sec : null;
  }

  setup(timings: WordTiming[]) {
    this.words = timings
      .map((t) => ({ word: cleanWord(t.word), start: t.start, end: t.end, state: "upcoming" as WordState }))
      .filter((w) => w.word.length > 0);
    this.reset();
  }
  reset() {
    this.currentWordIndex = 0; this.lastHighlightedIndex = -1;
    this.wordHighlightStartTime = 0; this.allWordsCompleted = false; this.visible = [];
    this.words.forEach((w) => (w.state = "upcoming"));
  }

  update(time: number): VisibleWord[] {
    if (this.words.length === 0 || this.allWordsCompleted) { this.visible = []; return this.visible; }

    const last = this.words[this.words.length - 1];
    const doneAt = this.completionSec ?? last.end;
    if (this.currentWordIndex >= this.words.length - 1 && time >= doneAt) {
      this.allWordsCompleted = true; this.visible = []; return this.visible;
    }

    const newIndex = this.findCurrentWordIndex(time);
    if (this.allWordsCompleted) { this.visible = []; return this.visible; }

    if (newIndex !== this.currentWordIndex) { this.updateWordStates(newIndex); this.currentWordIndex = newIndex; }
    this.updateVisibleWords();
    return this.visible;
  }

  private findCurrentWordIndex(time: number): number {
    // Find the "natural" current word: the latest word whose .start <= time.
    // NO start-side buffer — a word never becomes "current" before it begins.
    // In a silence gap (prevWord.end < time < nextWord.start), prevWord is
    // naturally held as current. Before the first word starts (leading silence),
    // returns -1 so nothing is highlighted.
    // TIMING_BUFFER is intentionally not used on the start side; the hold-previous
    // behavior across gaps makes an end-side buffer unnecessary.
    let natural = -1;
    for (let i = this.words.length - 1; i >= 0; i--) {
      if (time >= this.words[i].start) { natural = i; break; }
    }

    // Leading silence: no word has started yet — nothing is current.
    // Reset tracking so the first real word initialises cleanly.
    if (natural === -1) {
      this.lastHighlightedIndex = -1;
      return -1;
    }

    // First time we have a valid natural word: initialise tracking.
    if (this.lastHighlightedIndex === -1) {
      this.lastHighlightedIndex = natural; this.wordHighlightStartTime = time; return natural;
    }

    const cur = this.currentWordIndex;
    const timeOnCurrent = time - this.wordHighlightStartTime;
    // min-highlight anti-flicker guard — only when time is moving forward
    if (timeOnCurrent >= 0 && timeOnCurrent < CAPTION.MIN_HIGHLIGHT_DURATION && cur === this.lastHighlightedIndex) return cur;

    if (natural > this.lastHighlightedIndex) {
      // Catch-up resync: on a LARGE forward jump, snap directly to the natural word —
      // the never-skip ratchet below can only pay down backlog at 1 word/150ms, which
      // reads as captions lagging the voice for seconds after a clock jump.
      const backlog = natural - this.lastHighlightedIndex;
      const nextStart = this.words[this.lastHighlightedIndex + 1]?.start ?? time;
      if (backlog > CAPTION.CATCH_UP_MAX_LAG_WORDS || time - nextStart > CAPTION.CATCH_UP_MAX_LAG_SEC) {
        this.lastHighlightedIndex = natural; this.wordHighlightStartTime = time; return natural;
      }
      // never-skip: advance exactly one word per update, no matter the overshoot.
      // Safe: next ≤ natural, and words are ordered, so words[next].start ≤ time.
      const next = this.lastHighlightedIndex + 1;
      if (next < this.words.length) { this.lastHighlightedIndex = next; this.wordHighlightStartTime = time; return next; }
    }
    if (natural === this.lastHighlightedIndex) return this.lastHighlightedIndex;
    // backward seek: jump directly to the natural position
    if (natural < this.lastHighlightedIndex) {
      this.lastHighlightedIndex = natural; this.wordHighlightStartTime = time; return natural;
    }
    return this.lastHighlightedIndex;
  }

  private updateWordStates(newIndex: number) {
    for (let i = 0; i < this.words.length; i++) {
      this.words[i].state = i < newIndex ? "past" : i === newIndex ? "current" : "upcoming";
    }
  }

  private updateVisibleWords() {
    const cur = this.currentWordIndex;
    const N = this.windowSize;
    const inWindow = this.visible.some((v) => v.index === cur);
    if (this.visible.length > 0 && inWindow) {
      this.visible = this.visible.map((v) => ({
        ...this.words[v.index], index: v.index,
        state: (v.index < cur ? "past" : v.index === cur ? "current" : "upcoming") as WordState,
      }));
      return;
    }
    let start = Math.max(0, Math.min(cur, this.words.length - N));
    let end = Math.min(this.words.length, start + N);

    if (start < this.words.length && end > start) {
      if (this.words[end - 1].word.endsWith("-")) end = Math.max(start + 1, end - 1);
      for (let i = start; i < end; i++) {
        if (isEndOfSentence(this.words[i].word) && i + 1 < end) {
          if (cur <= i) { end = i + 1; break; }
          else { start = Math.max(0, cur); end = Math.min(this.words.length, start + N); break; }
        }
      }
    }
    this.visible = [];
    for (let i = start; i < end; i++) {
      this.visible.push({ ...this.words[i], index: i, state: i < cur ? "past" : i === cur ? "current" : "upcoming" });
    }
  }
}
